import { useToastStore } from "&/toast";
import { useSplashStore } from "&/splash";
import { onTouchCancel, onTouchEnd, onTouchStart } from "%/touch";
import { Splash } from "@/Splash";
import { ThemedToast } from "@/Toast";
import {
  createRootRoute,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import {
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { SELECTOR } from "%/constant";
import { SidePanels } from "@/common/SidePanels";
import { useBackground } from "&/background";
import { useLoadingStore } from "&/loading";
import { usePushDeepLinkRouter } from "^/common/usePushDeepLinkRouter";
import { Loading } from "@/Loading";
import ScrollToTop from "@/common/ScrollToTop";
import { AppExitDialog } from "@/AppExitDialog";
import { AppVersionGate } from "@/common/AppVersionGate";

const Error = () => {
  const { history } = useRouter();
  useEffect(() => {
    const sti = setTimeout(() => {
      history.push("/");
    }, 1000);
    return () => {
      clearTimeout(sti);
    };
  }, [history]);
  return <Splash visible />;
};

const ToastList = () => {
  const { toasts, remove } = useToastStore();
  return toasts.map((x) => <ThemedToast {...x} key={x.id} onClose={remove} />);
};

const RootComponent = () => {
  const { background } = useBackground();
  const { splash } = useSplashStore();
  const { loading } = useLoadingStore();
  const [showExitDialog, setShowExitDialog] = useState(false);
  usePushDeepLinkRouter();

  // 뒤로가기 핸들러를 저장할 ref (항상 최신 상태를 참조하기 위함)
  const backHandlerRef = useRef<() => void>(() => {});

  // 다이얼로그 상태를 동기적으로 추적하는 ref (React 리렌더 타이밍 문제 해결)
  const showExitDialogRef = useRef(false);

  // 상태와 ref를 동시에 업데이트하는 함수
  const setShowExitDialogSync = (value: boolean) => {
    showExitDialogRef.current = value;
    setShowExitDialog(value);
  };

  // 전역 앱 종료 함수
  const handleAppExit = () => {
    const userAgent = navigator.userAgent;
    if (/MT_A/i.test(userAgent)) {
      try {
        if (window.mung && typeof window.mung.appFinish === "function") {
          window.mung.appFinish();
          console.log("안드로이드 appFinish 호출 완료");
        } else {
          console.error("window.mung.appFinish 함수를 찾을 수 없음");
        }
      } catch (e) {
        console.error("안드로이드 앱 종료 호출 실패:", e);
      }
    }
  };

  // 전역 뒤로가기 처리 함수
  // const handleGlobalBackButton = () => {
  //   const currentPath = window.location.pathname;
  //   console.log("전역 뒤로가기 처리 - 현재 경로:", currentPath);

  //   // Gallery 페이지인지 확인
  //   if (
  //     currentPath === "/business/layout/gallery" ||
  //     currentPath === "/customer/layout/gallery"
  //   ) {
  //     // Gallery 페이지는 개별 로직에서 처리
  //     console.log("Gallery 페이지 - 개별 로직 호출");
  //     if ((window as any).galleryBackHandler) {
  //       (window as any).galleryBackHandler();
  //     }
  //     return;
  //   }

  //   // 메인 페이지들에서 앱 종료 확인 표시
  //   const mainPages = [
  //     "/business/layout/to-school",
  //     "/business/layout/notify",
  //     "/business/layout/calendar",
  //     "/business/layout/user",
  //     "/business/layout/teacher",
  //     "/business/layout/user-teacher-setting",
  //     "/customer/layout/home",
  //     "/customer/layout/inform",
  //     "/customer/layout/calendar",
  //     "/customer/layout/user",
  //   ];

  //   if (mainPages.includes(currentPath)) {
  //     console.log("메인 페이지 - 앱 종료 확인 표시");
  //     setShowExitDialog(true);
  //   }
  // };

  // DeepLink 처리 함수를 useCallback으로 감싸기
  // const handleDeepLink = useCallback(
  //   (path: string, query: string, type: string) => {
  //     setTimeout(() => {
  //       alert("handleDeepLink 호출됨!");
  //     }, 2000);
  //     if (type === "PUSH_NOTICE_USER") {
  //       const targetPath = "/customer/layout/inform-detail";
  //       const fullUrl = query ? `${targetPath}?${query}` : targetPath;
  //       history.push(fullUrl);
  //     } else if (type === "PUSH_NOTICE_BUSINESS") {
  //       const targetPath = "/business/layout/notify-detail";
  //       const fullUrl = query ? `${targetPath}?${query}` : targetPath;
  //       history.push(fullUrl); // 추가 필요
  //     } else if (type === "PUSH_RESERV_USER") {
  //       const targetPath = "/customer/layout/calendar-reservation-chg";
  //       const fullUrl = query ? `${targetPath}?${query}` : targetPath;
  //       history.push(fullUrl); // 추가 필요
  //     } else if (type === "PUSH_RESERV_BUSINESS") {
  //       console.log("PUSH_RESERV_BUSINESS:", { path, query, type });
  //       alert("PUSH_RESERV_BUSINESS: " + JSON.stringify({ path, query, type }));

  //       /* // 실제 이동할 페이지 경로를 정의하고 구현 필요
  //         const targetPath = "/business/layout/reservation-detail"; // 예시
  //         const fullUrl = query ? `${targetPath}?${query}` : targetPath;
  //         history.push(fullUrl); */
  //     }
  //   },
  //   [history]
  // );

  const setVhVariable = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  // 뒤로가기 핸들러 ref 업데이트 (매 렌더마다 최신 상태 반영)
  backHandlerRef.current = () => {
    // 경로 정규화: 끝의 슬래시 제거
    const rawPath = window.location.pathname;
    const currentPath = rawPath.endsWith("/") && rawPath.length > 1
      ? rawPath.slice(0, -1)
      : rawPath;

    console.log("=== 뒤로가기 핸들러 호출 ===");
    console.log("원본 경로:", rawPath);
    console.log("정규화된 경로:", currentPath);
    console.log("현재 showExitDialog 상태 (ref):", showExitDialogRef.current);
    console.log("customBackHandler 존재:", !!window.customBackHandler);

    // 이미 다이얼로그가 표시 중이면 무시 (중복 호출 방지) - ref로 동기적 체크
    if (showExitDialogRef.current) {
      console.log("다이얼로그 이미 표시 중 - 무시");
      return;
    }

    // 커스텀 핸들러가 있으면 우선 실행
    if (window.customBackHandler) {
      console.log("커스텀 핸들러 실행");
      window.customBackHandler();
      return;
    }

    // 앱 종료 다이얼로그를 표시할 진짜 홈 화면만
    const mainPages = [
      "/business/layout/home",       // 사업자앱 홈
      "/customer/layout/home",       // 고객앱 홈
    ];

    // 메인 페이지 체크 (정규화된 경로로 비교)
    const isMainPage = mainPages.some(page =>
      currentPath === page || currentPath.toLowerCase() === page.toLowerCase()
    );
    console.log("메인 페이지 여부:", isMainPage);

    if (isMainPage) {
      // 메인 페이지면 앱 종료 다이얼로그 표시
      console.log(">>> 앱 종료 다이얼로그 표시");
      setShowExitDialogSync(true);
    } else {
      // 그 외 페이지면 뒤로가기
      console.log(">>> history.back() 호출");
      window.history.back();
    }
  };

  useEffect(() => {
    setVhVariable();
    window.addEventListener("resize", setVhVariable);

    window.addEventListener("touchstart", onTouchStart, { passive: false });
    // window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchCancel);
    window.addEventListener(
      "gesturestart",
      (e) => {
        e.preventDefault();
      },
      { passive: false, capture: true }
    );

    // 안드로이드 뒤로가기 버튼 처리 함수 등록
    const userAgent = navigator.userAgent;
    const isAndroidApp = /MT_A/i.test(userAgent);

    if (isAndroidApp) {
      // 페이지별 커스텀 핸들러 저장소 초기화
      window.customBackHandler = null;

      // fnHistoryBack: ref를 통해 항상 최신 핸들러 호출
      window.fnHistoryBack = () => {
        console.log("fnHistoryBack 호출");
        backHandlerRef.current();
      };
      console.log("window.fnHistoryBack 설정 완료 (ref 패턴)");
    }
  }, []);

  const hideExitConfirmation = () => {
    console.log(">>> 다이얼로그 취소 - showExitDialogRef를 false로 설정");
    setShowExitDialogSync(false);
  };

  const confirmAppExit = () => {
    setShowExitDialogSync(false);
    handleAppExit();
  };

  // VITE_APP_TYPE 으로 빌드별 배경 톤 결정
  const isBusinessApp = import.meta.env.VITE_APP_TYPE === "business";
  const bgClass = isBusinessApp
    ? "bg-gradient-to-br from-[#F5F3FF] via-[#FAFAFC] to-white"
    : "bg-gradient-to-br from-[#FFF4E6] via-[#FFFAF2] to-white";

  return (
    <div
      className={`w-full h-screen overflow-hidden md:py-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_480px_minmax(0,1fr)] ${bgClass}`}
    >
      {/* PC(md+) 좌·우 사이드 패널 — VITE_APP_TYPE 에 따라 보호자/사업자 디자인 */}
      <SidePanels />

      {/* 중앙 모바일폭 본문 (480px) — grid 1행 2열에 명시 배치, PC는 라운드 + 떠있는 카드
          data-content-area: OAuth 팝업 위치 보정에 사용 (useOAuthPopup) */}
      <div data-content-area className="relative z-10 md:row-start-1 md:col-start-2 w-full h-dvh md:h-full flex flex-col bg-[#F5F5F5] overflow-hidden md:rounded-3xl md:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.18)]">
        <Suspense>
          <div
            className="flex flex-col flex-1 min-h-0"
            style={{ background }}
            id={SELECTOR.APP}
          >
            <div id={SELECTOR.HEADER} />
            <main className="flex flex-col flex-1 min-h-0 bg-[#F5F5F5]" id={SELECTOR.MAIN}>
              <AppVersionGate>
                <Outlet />
              </AppVersionGate>
            </main>
            <div id={SELECTOR.NAV} />
          </div>
        </Suspense>
        <Loading visible={loading} />
        <Splash visible={splash} className="z-40" />
      </div>

      <ScrollToTop />
      <ToastList />
      <AppExitDialog
        visible={showExitDialog}
        onConfirm={confirmAppExit}
        onCancel={hideExitConfirmation}
      />
    </div>
  );
};

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: Error,
  notFoundComponent: Error,
});
